import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { useNavigate } from 'react-router-dom';
import {
    ArrowRight, TrendingUp, AlertTriangle, FileText, Coins,
    Activity, Users, Award, Clock, ShieldAlert, Layers,
    Sparkles, RefreshCw, Download, ChevronDown, CheckCircle2,
    Zap, ArrowUpRight, ArrowDownRight, Minus, Calendar,
    BarChart2, Target, TrendingDown, X, ShieldCheck, Inbox, Check
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, AreaChart, Area, Cell, LineChart, Line, Legend,
    PieChart, Pie
} from 'recharts';
import { formatRevenue, getStageMeta } from '../dashboardConstants';
import { Avatar } from '../components/DashboardComponents';
import './Dashboard.css';
import DashboardExportButton from '../components/DashboardExportButton';

// ─── Date Formatting and Conversion Helpers ─────────────────────────────────────
const toDisplayDate = (isoStr) => {
    if (!isoStr) return '';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(isoStr)) return isoStr;
    const parts = isoStr.split('-');
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return isoStr;
};

const toApiDate = (displayStr) => {
    if (!displayStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(displayStr)) return displayStr;
    const parts = displayStr.split('/');
    if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
    }
    return displayStr;
};

const formatDateDMY = (dateInput) => {
    if (!dateInput) return '—';
    try {
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) return String(dateInput);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    } catch {
        return String(dateInput);
    }
};

// ─── Custom Tooltip Component ──────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, unit = '' }) => {
    if (active && payload && payload.length) {
        return (
            <div className="db-chart-tooltip">
                <p className="db-tooltip-label">{label}</p>
                {payload.map((p, i) => (
                    <p key={i} className="db-tooltip-value" style={{ color: p.color || '#FF5526' }}>
                        {p.value}{unit}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

// ─── Custom Pie Tooltip Component ──────────────────────────────────────────────
const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="db-chart-tooltip" style={{ border: 'none' }}>
                <p className="db-tooltip-label" style={{ fontWeight: 700, color: '#FFFFFF' }}>{data.name}</p>
                <p className="db-tooltip-value" style={{ color: data.color || '#FF5526', margin: 0 }}>
                    {data.value} {data.value === 1 ? 'Proposal' : 'Proposals'}
                </p>
            </div>
        );
    }
    return null;
};

// ─── Custom Axis Tick Component ────────────────────────────────────────────────
const CustomAxisTick = ({ x, y, payload, isManager }) => {
    const value = payload.value;
    if (!value) return null;

    let lines = [];
    const upperVal = String(value).toUpperCase();

    if (!isManager) {
        // Explicit clean mapping for Team Member's Section stages
        if (upperVal.includes('ASSIGNED')) {
            lines = ['Assigned'];
        } else if (upperVal.includes('SUBMITTED') || upperVal.includes('SUBMIT')) {
            lines = ['Submitted'];
        } else if (upperVal.includes('APPROVED')) {
            lines = ['Approved'];
        } else if (upperVal.includes('REVISION')) {
            lines = ['Revision'];
        } else if (upperVal.includes('PROGRESS')) {
            lines = ['In Progress'];
        } else {
            lines = [String(value)];
        }
    } else {
        // Standard Manager/Global Proposal stages
        if (upperVal.includes('ACCEPTED') || upperVal.includes('WON')) {
            lines = ['Accepted'];
        } else if (upperVal.includes('REJECTED') || upperVal.includes('LOST')) {
            lines = ['Rejected'];
        } else if (upperVal.includes('SUBMITTED') || upperVal.includes('SUBMIT')) {
            lines = ['Submitted'];
        } else if (upperVal.includes('GENERATING')) {
            lines = ['Generating'];
        } else if (upperVal.includes('ANALYZ')) {
            lines = ['Analyzed'];
        } else if (upperVal.includes('REVIEW')) {
            lines = ['Review'];
        } else if (upperVal.includes('PROGRESS')) {
            lines = ['In Progress'];
        } else if (upperVal.includes('FINALISED') || upperVal.includes('FINALIZED')) {
            lines = ['Finalized'];
        } else if (upperVal.includes('NO-GO')) {
            lines = ['No-Go'];
        } else if (upperVal.includes('PENDING')) {
            lines = ['Pending'];
        } else if (upperVal.includes('EXPIRED')) {
            lines = ['Expired'];
        } else {
            lines = [String(value)];
        }
    }

    return (
        <g transform={`translate(${x},${y})`}>
            {lines.map((line, index) => (
                <text
                    key={index}
                    x={0}
                    y={index * 12 + 10}
                    textAnchor="middle"
                    fill="#64748B"
                    style={{
                        fontSize: '9.5px',
                        fontWeight: '600',
                        fontFamily: "'Inter', sans-serif"
                    }}
                >
                    {line}
                </text>
            ))}
        </g>
    );
};

// ─── KPI Card Component ────────────────────────────────────────────────────────
const KpiCard = ({ title, value, icon: Icon, trend, trendValue, colorClass, loading, tooltip }) => {
    const trendUp = trend === 'up';
    const trendFlat = trend === 'flat';

    if (loading) {
        return <div className="db-kpi-card db-kpi-skeleton" />;
    }

    return (
        <div className={`db-kpi-card db-kpi-${colorClass}`} title={tooltip}>
            <div className="db-kpi-top">
                <span className="db-kpi-label">{title}</span>
                <div className={`db-kpi-icon-wrap db-icon-${colorClass}`}>
                    <Icon size={16} strokeWidth={2.2} />
                </div>
            </div>
            <div className="db-kpi-value">{value}</div>
            {trendValue !== undefined && (
                <div className={`db-kpi-trend ${trend}`}>
                    {trendFlat ? <Minus size={12} /> : trendUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    <span>{trendValue}</span>
                </div>
            )}
        </div>
    );
};

const getSectionStatusLabel = (statusCode) => {
    switch (String(statusCode)) {
        case '51': return 'Assigned';
        case '52': return 'In-Progress';
        case '53': return 'Submitted';
        case '54': return 'Approved';
        case '55': return 'Revision';
        default: return 'Unknown Status';
    }
};

const getStatusBadgeClass = (statusCode) => {
    switch (String(statusCode)) {
        case '51': return 'status-underloaded'; // Gray/Blue
        case '52': return 'status-balanced'; // Green
        case '53': return 'status-balanced'; // Green
        case '54': return 'status-available'; // Green
        case '55': return 'status-overloaded'; // Red
        default: return 'status-underloaded';
    }
};

const getSingleTitle = (title) => {
    if (!title) return 'N/A';
    let val = Array.isArray(title) ? title[0] : title;
    if (typeof val === 'string') {
        const parts = val.split(/[;,|\n]/);
        val = parts[0] || val;
    }
    return String(val).trim().toUpperCase();
};

// ─── Alert Item ────────────────────────────────────────────────────────────────
const AlertItem = ({ item, type, onClick }) => {
    const isDeadline = type === 'deadline';
    const titleText = item.proposal_title || item.title || 'Unknown Title';
    const subText = isDeadline ? (item.client_name || `Status: ${getSectionStatusLabel(item.status_code)}`) : <>Stuck in <strong>{item.stuck_stage}</strong></>;

    return (
        <div className={`db-alert-item ${isDeadline ? 'db-alert-deadline' : 'db-alert-bottleneck'}`} onClick={onClick}>
            <div className="db-alert-stripe" />
            <div className="db-alert-body">
                <div className="db-alert-row">
                    <span className="db-alert-title" style={{ border: 'none', boxShadow: 'none', background: 'transparent', padding: 0 }}>
                        {item.proposal_title ? '' : <span style={{ color: '#94A3B8', fontWeight: 600, marginRight: '4px' }}>SECTION:</span>}
                        {getSingleTitle(titleText)}
                    </span>
                    <span className={`db-severity-badge ${isDeadline ? 'sev-high' : 'sev-critical'}`}>
                        {isDeadline ? 'HIGH' : 'CRITICAL'}
                    </span>
                </div>
                <span className="db-alert-sub">
                    {subText}
                </span>
                <div className="db-alert-meta">
                    {isDeadline
                        ? <><Clock size={11} /><span>Due: {item.hours_left !== undefined ? `${item.hours_left}h left · ` : ''}{formatDateDMY(item.deadline)}</span></>
                        : <><AlertTriangle size={11} /><span>{item.days_stuck} days stalled</span></>
                    }
                    <button className="db-alert-cta">View →</button>
                </div>
            </div>
        </div>
    );
};

// ─── Team Member Card ──────────────────────────────────────────────────────────
// Capitalise the first letter of every word ("girish c" -> "Girish C"). Other letters are left as typed.
const capitalizeWords = (value) =>
    String(value ?? '').trim().replace(/(^|\s)(\S)/g, (_, space, ch) => space + ch.toUpperCase());

// Roles may come as "employee" or "team_lead": show them as "Employee" / "Team Lead".
const formatRole = (value) => capitalizeWords(String(value ?? '').replace(/[_-]+/g, ' '));

const TeamMemberCard = ({ member, rfpList, isFull = false, onViewAll }) => {
    const sections = member.active_sections_count ?? member.total_active_sections ?? member.sections_count ?? 0;
    const completionRate = member.completion_rate ?? member.metrics?.completion_rate;
    const dueThisWeek = member.due_this_week ?? member.metrics?.due_this_week;
    const sectionsInRevision = member.sections_in_revision ?? member.metrics?.sections_in_revision;

    // Determine utilization status based on active sections count
    let status = 'available';
    if (sections >= 0 && sections <= 5) {
        status = 'available';
    } else if (sections >= 6 && sections <= 10) {
        status = 'balanced';
    } else if (sections > 10) {
        status = 'overloaded';
    } else {
        const rawStatus = (member.utilization_status || 'Available').toLowerCase();
        if (rawStatus.includes('balanced')) {
            status = 'balanced';
        } else if (rawStatus.includes('overloaded')) {
            status = 'overloaded';
        } else {
            status = 'available';
        }
    }

    // workload bar: treat 10 as max
    const workloadPct = Math.min(100, (sections / 10) * 100);

    const statusConfig = {
        balanced: { label: 'Balanced', cls: 'status-balanced' },
        available: { label: 'Available', cls: 'status-available' },
        overloaded: { label: 'Overloaded', cls: 'status-overloaded' },
        underloaded: { label: 'Underloaded', cls: 'status-underloaded' },
    };
    const cfg = statusConfig[status] || statusConfig.available;

    const rfpTitles = member.driving_rfp_titles || [];
    const colors = [
        { text: '#C94E14', icon: '#C94E14' }, // Orange
        { text: '#D63F15', icon: '#D63F15' }, // Blue
        { text: '#7C3AED', icon: '#7C3AED' }, // Purple
        { text: '#15803D', icon: '#15803D' }  // Green
    ];
    const getProposalColor = (index) => colors[index % colors.length];

    return (
        <div className="db-team-card">
            <div className="db-team-card-top">
                <Avatar name={capitalizeWords(member.full_name)} size={28} />
                <div className="db-team-info">
                    <h5 className="db-team-name">{capitalizeWords(member.full_name)}</h5>
                    <span className="db-team-role">{formatRole(member.role) || 'Contributor'}</span>
                </div>
                <span className={`db-team-status-badge ${cfg.cls}`}>{cfg.label}</span>
            </div>

            <div className="db-team-workload" title={`${sections} active section(s) currently assigned (Assigned, In Progress, Submitted, or Revision Needed)`}>
                <div className="db-workload-header">
                    <span>Workload</span>
                    <span className="db-workload-count">{sections} {sections === 1 ? 'Active Section' : 'Active Sections'}</span>
                </div>
                <div className="db-workload-bar-track">
                    <div
                        className={`db-workload-bar-fill ${cfg.cls}`}
                        style={{ width: `${workloadPct}%` }}
                    />
                </div>
                {(completionRate !== undefined || dueThisWeek !== undefined || sectionsInRevision !== undefined) && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px', fontSize: '11px', color: '#64748B', flexWrap: 'wrap' }}>
                        {completionRate !== undefined && (
                            <span title="(Total Submitted or Approved / Total Assigned) * 100">
                                <strong>{completionRate}%</strong> completed
                            </span>
                        )}
                        {dueThisWeek !== undefined && Number(dueThisWeek) > 0 && (
                            <span title="Pending sections due by Sunday" style={{ color: '#D97706' }}>
                                • <strong>{dueThisWeek}</strong> due this week
                            </span>
                        )}
                        {sectionsInRevision !== undefined && Number(sectionsInRevision) > 0 && (
                            <span title="Sections where changes were requested" style={{ color: '#DC2626' }}>
                                • <strong>{sectionsInRevision}</strong> in revision
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="db-team-proposals-section" style={{ marginTop: '8px' }}>
                <h6 className="db-proposals-section-title" style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Assigned Proposals
                </h6>
                {rfpTitles.length > 0 ? (
                    <div className="db-team-proposals-list" style={{ display: 'flex', flexDirection: 'column', gap: isFull ? '8px' : '6px' }}>
                        {(isFull ? rfpTitles : rfpTitles.slice(0, 2)).map((t, i, arr) => {
                            const colorCfg = getProposalColor(i);
                            const matchedRfp = rfpList?.find(r =>
                                (r.proposal_title || r.proposal_name || r.extraction_data?.proposal_title) === t
                            );
                            const clientName = matchedRfp?.client_name;
                            return (
                                <div
                                    key={i}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '2px',
                                        paddingBottom: isFull && i < arr.length - 1 ? '8px' : '0',
                                        borderBottom: isFull && i < arr.length - 1 ? '1px solid #F1F5F9' : 'none'
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '6px',
                                            fontSize: '11.5px',
                                            fontWeight: '600',
                                            color: colorCfg.text,
                                            lineHeight: '1.4'
                                        }}
                                        title={t}
                                    >
                                        <FileText size={12} style={{ color: colorCfg.icon, flexShrink: 0, marginTop: '2px' }} />
                                        <span style={{ wordBreak: 'break-word' }}>{t}</span>
                                    </div>
                                    {isFull && clientName && (
                                        <span style={{ fontSize: '9.5px', color: '#94A3B8', marginLeft: '18px', fontWeight: '500' }}>
                                            Client: {clientName}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                        {!isFull && rfpTitles.length > 2 && (
                            <div
                                onClick={onViewAll}
                                style={{
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    color: '#64748B',
                                    cursor: onViewAll ? 'pointer' : 'default',
                                    userSelect: 'none',
                                    padding: '2px 0'
                                }}
                            >
                                +{rfpTitles.length - 2} More
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ fontSize: '12px', color: '#94A3B8', padding: '4px 0', fontStyle: 'italic' }}>
                        No assignments are assigned.
                    </div>
                )}
            </div>
        </div>
    );
};

const ProposalBoardCard = ({ proposal, isManager, onClick }) => {
    const stage = proposal.status || proposal.proposal_stage || 'Drafting';
    const meta = getStageMeta(stage);
    const teamMembers = proposal.team_members || (proposal.created_by ? [proposal.created_by] : []);

    const deadlineText = isManager
        ? (proposal.proposal_deadline || proposal.deadline_display || 'TBD')
        : (proposal.proposal_deadline || proposal.deadline_display || (proposal.deadline ? new Date(proposal.deadline).toLocaleDateString() : (proposal.target_date ? new Date(proposal.target_date).toLocaleDateString() : 'TBD')));

    return (
        <div className="db-proposal-card" onClick={onClick}>
            <div className="db-prop-header">
                <div className="db-prop-titles">
                    <h4 className="db-prop-name" style={{ border: 'none', boxShadow: 'none', background: 'transparent', padding: 0 }}>
                        {getSingleTitle(proposal.proposal_title || proposal.proposal_name || proposal.extraction_data?.proposal_title || 'Untitled')}
                    </h4>
                    <span className="db-prop-client">{proposal.client_name || '—'}</span>
                </div>
                {/* <span className="db-prop-badge" style={{ background: meta.bg, color: meta.text, borderColor: meta.border }}>
                    {meta.displayName}
                </span> */}
            </div>

            <div className="db-prop-meta">
                <div className="db-prop-date">
                    {isManager ? <Clock size={12} /> : <Calendar size={12} />}
                    <span>{deadlineText}</span>
                </div>
            </div>

            <div className="db-prop-footer">
                {teamMembers.length > 0 ? (
                    <div className="db-prop-avatars">
                        {teamMembers.slice(0, 3).map((m, i) => (
                            <Avatar key={i} name={m} size={22} />
                        ))}
                    </div>
                ) : <div />}
                <div className="db-prop-actions">
                    <button className="db-prop-action-btn" onClick={e => { e.stopPropagation(); onClick(); }}>
                        Open <ArrowRight size={11} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Section Card Shell ────────────────────────────────────────────────────────
const SectionCard = ({ title, icon: Icon, iconClass, badge, action, children, className = '' }) => (
    <div className={`db-section-card ${className}`}>
        <div className="db-section-header">
            <div className="db-section-title-row">
                {Icon && (
                    <div className={`db-section-icon ${iconClass || ''}`}>
                        <Icon size={16} />
                    </div>
                )}
                <h3 className="db-section-title">{title}</h3>
                {badge}
            </div>
            {action}
        </div>
        <div className="db-section-body">{children}</div>
    </div>
);

// ─── Skeleton ──────────────────────────────────────────────────────────────────
const Skeleton = ({ height = 60, radius = 8, count = 1 }) => (
    <>
        {[...Array(count)].map((_, i) => (
            <div key={i} className="db-skeleton" style={{ height, borderRadius: radius, marginBottom: 12 }} />
        ))}
    </>
);

// ─── Main Dashboard ────────────────────────────────────────────────────────────
const Dashboard = ({ user: propUser }) => {
    const navigate = useNavigate();
    const [activeStageIndex, setActiveStageIndex] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [loadingMetrics, setLoadingMetrics] = useState(true);
    const [rfpList, setRfpList] = useState([]);
    const [loadingRFPs, setLoadingRFPs] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showTeamModal, setShowTeamModal] = useState(false);
    const [selectedTeamMember, setSelectedTeamMember] = useState(null);
    const [showAlertsModal, setShowAlertsModal] = useState(false);
    const [showSectionsModal, setShowSectionsModal] = useState(false);

    useEffect(() => {
        if (showTeamModal || showAlertsModal || showSectionsModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showTeamModal, showAlertsModal, showSectionsModal]);

    // Date filter states matching backend expectations
    const [preset, setPreset] = useState('all');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const fromPickerRef = useRef(null);
    const toPickerRef = useRef(null);

    const user = propUser || JSON.parse(localStorage.getItem('user') || '{}');
    const userDesignation = (user?.designation || '').toLowerCase();
    const userRole = (user?.role || '').toLowerCase();
    const isManager = ['lead', 'manager', 'admin'].some(r =>
        userDesignation.includes(r) || userRole.includes(r)
    );

    const presetOptions = [
        { value: 'all', label: 'All' },
        { value: 'this_week', label: 'This Week' },
        { value: 'this_month', label: 'This Month' },
        { value: 'last_month', label: 'Last Month' },
        { value: 'last_3_months', label: 'Last 3 Months' },
        { value: 'last_6_months', label: 'Last 6 Months' },
        { value: 'this_year', label: 'This Year' },
        { value: 'last_year', label: 'Last Year' },
        { value: 'custom', label: 'Custom Range' }
    ];

    const fetchRFPs = useCallback(async () => {
        try {
            if (isManager) {
                const res = await api.get('/rfp/user/all');
                if (res.status === 200) setRfpList(res.data.rfps || []);
            } else {
                const fb = await api.get('/rfp/employee/my-assignments');
                const groups = fb.data?.groups || [];
                setRfpList(groups.map(g => ({
                    rfp_id: g.rfp_id, id: g.rfp_id,
                    proposal_name: g.proposal_title,
                    client_name: g.client_name,
                    status: g.global_status || 'Drafting',
                    confidence: g.confidence
                })));
            }
        } catch {
            // silent fallback
        } finally {
            setLoadingRFPs(false);
        }
    }, [isManager]);

    const fetchData = useCallback(async (currentPreset = preset, fDate = fromDate, tDate = toDate) => {
        setLoadingMetrics(true);
        try {
            let url = isManager ? '/dashboard/metrics' : '/dashboard/employee-metrics';
            const params = new URLSearchParams();
            if (currentPreset === 'custom') {
                const apiFrom = toApiDate(fDate);
                const apiTo = toApiDate(tDate);
                if (apiFrom) params.append('from_date', apiFrom);
                if (apiTo) params.append('to_date', apiTo);
            } else {
                params.append('preset', currentPreset);
            }
            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }

            const res = await api.get(url);
            if (res.status === 200) {
                setMetrics(res.data);
                if (res.data.active_proposals !== undefined && res.data.active_proposals !== null) {
                    setRfpList(res.data.active_proposals);
                    setLoadingRFPs(false);
                } else {
                    await fetchRFPs();
                }
            } else {
                await fetchRFPs();
            }
        } catch {
            await fetchRFPs();
        } finally {
            setLoadingMetrics(false);
            setRefreshing(false);
        }
    }, [fetchRFPs]);

    useEffect(() => {
        fetchData('all');
    }, [fetchData]);



    const handleRefresh = () => {
        setRefreshing(true);
        fetchData(preset, fromDate, toDate);
    };

    // ── Data extraction ─────────────────────────────────────────────────────────
    const kpis = metrics?.section_a_kpis || {};
    const activeRFPs = kpis.total_active_rfps ?? metrics?.active_rfps ?? 0;
    const winRate = kpis.win_rate_percentage ?? metrics?.win_rate ?? 0;

    // Support the new schema's pre-formatted currency strings, falling back to complete integers with currency prefix
    const currency = kpis.currency || metrics?.currency || 'AED';

    const getFormattedKpiValue = (rawVal, completeVal, formattedStr, fallbackVal) => {
        if (formattedStr) return formattedStr;

        const finalVal = completeVal !== undefined ? completeVal : (rawVal !== undefined ? rawVal : fallbackVal);
        if (finalVal === undefined || finalVal === null) return `${currency} 0`;

        const strVal = String(finalVal).trim();
        // If the value already includes the currency name/symbol, return it as-is
        if (strVal.includes(currency) || /^[A-Za-z]/.test(strVal)) {
            return strVal;
        }

        // Check if it's a valid number. If so, format it as a complete integer with commas
        const num = Number(strVal.replace(/[^0-9.-]/g, ''));
        if (!isNaN(num)) {
            return `${currency} ${num.toLocaleString()}`;
        }

        return `${currency} ${strVal}`;
    };

    const pipelineValDisplay = getFormattedKpiValue(
        kpis.total_pipeline_value,
        kpis.total_pipeline_value_complete,
        kpis.total_pipeline_value_formatted,
        metrics?.total_revenue
    );

    const achievedValDisplay = getFormattedKpiValue(
        kpis.achieved_value,
        kpis.achieved_value_complete,
        kpis.achieved_value_formatted,
        metrics?.achieved_revenue
    );

    // Employee KPIs
    const activeSections = kpis.total_active_sections ?? kpis.active_sections_count ?? kpis.active_sections ?? 0;
    const completionRate = kpis.completion_rate ?? kpis.completion_rate_percent ?? 0;
    const dueThisWeek = kpis.due_this_week ?? kpis.due_this_week_count ?? 0;
    const sectionsInRevision = kpis.sections_in_revision ?? kpis.revision_needed_count ?? kpis.in_revision ?? 0;

    const urgentDeadlines = metrics?.section_b_alerts?.urgent_deadlines || [];
    const bottlenecks = metrics?.section_b_alerts?.bottlenecks || [];
    const hasAlerts = urgentDeadlines.length > 0 || bottlenecks.length > 0;

    const teamWorkload = metrics?.section_c_workload || [];
    const sortedTeamWorkload = [...teamWorkload].sort((a, b) => (b.active_sections_count || 0) - (a.active_sections_count || 0));
    const displayTeamWorkload = sortedTeamWorkload.slice(0, 2);

    const stageDistRaw = metrics?.section_d_analytics?.stage_distribution
        || metrics?.section_d_analytics?.status_distribution
        || metrics?.stage_distribution
        || metrics?.status_distribution
        || {};
    const winTrends = metrics?.section_d_analytics?.win_loss_trends || [];
    const prodTrends = metrics?.section_d_analytics?.productivity_trends || [];

    const STAGE_COLORS = ['#FF5526', '#3B82F6', '#06B6D4', '#64748B', '#10B981', '#EF4444', '#8B5CF6', '#F59E0B', '#EC4899', '#14B8A6'];
    const getStageColor = (name, index) => {
        if (!name) return STAGE_COLORS[index % STAGE_COLORS.length];
        const upper = String(name).toUpperCase();
        if (upper.includes('REVISION')) return '#EF4444'; // Bright Red for revision needed
        if (upper.includes('REJECTED') || upper.includes('LOST')) return '#EF4444';
        if (upper.includes('APPROVED') || upper.includes('ACCEPTED') || upper.includes('WON')) return '#10B981';
        if (upper.includes('NO-GO') || upper.includes('NOGO')) return '#EC4899';
        if (upper.includes('PENDING')) return '#8B5CF6';
        if (upper.includes('GENERATING')) return '#8B5CF6';
        if (upper.includes('ANALYZ') || upper.includes('UPLOAD')) return '#3B82F6';
        if (upper.includes('EXPIRE') || upper.includes('OVERDUE')) return '#64748B';
        if (upper.includes('PROGRESS') || upper.includes('WORKING') || upper.includes('DRAFT')) return '#06B6D4';
        if (upper.includes('SUBMIT')) return '#FF5526';
        if (upper.includes('ASSIGNED')) return '#FF5526';
        if (upper.includes('FINALIZ')) return '#F59E0B';
        return STAGE_COLORS[index % STAGE_COLORS.length];
    };

    const getNormalizedStageDistribution = () => {
        // Direct mapping of whatever BE sends — no extra fields added for either role
        if (Array.isArray(stageDistRaw)) {
            return stageDistRaw.map((item, index) => {
                const label = item.label || item.name || item.stage || item.status || `Stage ${index + 1}`;
                const count = Number(item.count ?? item.value ?? 0);
                return {
                    name: label,
                    label,
                    value: count,
                    count,
                    color: item.color || getStageColor(label, index)
                };
            });
        }

        if (typeof stageDistRaw === 'object' && stageDistRaw !== null) {
            return Object.entries(stageDistRaw).map(([key, val], index) => {
                let label = key;
                let count = 0;
                let color;

                if (typeof val === 'object' && val !== null) {
                    label = val.label || val.name || val.stage || key;
                    count = Number(val.count ?? val.value ?? 0);
                    color = val.color;
                } else {
                    count = Number(val ?? 0);
                }

                return {
                    name: label,
                    label,
                    value: count,
                    count,
                    color: color || getStageColor(label, index)
                };
            });
        }

        return [];
    };

    const stagePieData = getNormalizedStageDistribution();
    const totalRFPs = stagePieData.reduce((sum, item) => sum + item.value, 0);

    const activeAssignedSections = metrics?.active_sections || [];
    const sortedActiveSections = [...activeAssignedSections].sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
    const displayActiveSections = sortedActiveSections.slice(0, 2);

    // Combine and sort alerts
    const allAlertsRaw = [
        ...urgentDeadlines.map(d => ({ ...d, type: 'deadline' })),
        ...bottlenecks.map(b => ({ ...b, type: 'bottleneck' }))
    ];
    const allAlertsSorted = allAlertsRaw.sort((a, b) => {
        if (a.type === 'deadline' && b.type === 'deadline') return new Date(a.deadline) - new Date(b.deadline);
        if (a.type === 'bottleneck' && b.type === 'bottleneck') return b.days_stuck - a.days_stuck;
        return a.type === 'deadline' ? -1 : 1; // Deadlines before bottlenecks
    });

    const displayAlerts = allAlertsSorted.slice(0, 3);

    return (
        <div className="db-root" id="dashboard-export-root">

            {/* ── Header ─────────────────────────────────────────────────────── */}
            <header className="db-header">
                <div className="db-header-left">
                    <h1 className="db-page-title">Insights Hub</h1>
                    <p className="db-page-subtitle">Track proposal performance, deadlines from one place.</p>
                </div>
                <div className="db-header-actions">
                    <div className="db-date-filter-group">

                        <select
                            className="db-preset-select"
                            value={preset}
                            onChange={(e) => {
                                setPreset(e.target.value);
                            }}
                            disabled={refreshing || loadingMetrics}
                        >
                            {presetOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>

                        <div className="db-inline-date-inputs">
                            <div className="db-inline-input-wrap">
                                <span className="db-date-label">From:</span>
                                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        placeholder="DD/MM/YYYY"
                                        value={toDisplayDate(fromDate)}
                                        onChange={(e) => {
                                            setFromDate(e.target.value);
                                            setPreset('custom');
                                        }}
                                        disabled={preset !== 'custom' || refreshing || loadingMetrics}
                                        className="db-inline-date-picker"
                                        style={{ paddingRight: '32px', width: '130px' }}
                                    />
                                    <input
                                        type="date"
                                        ref={fromPickerRef}
                                        value={toApiDate(fromDate)}
                                        onChange={(e) => {
                                            const newFrom = e.target.value;
                                            setFromDate(toDisplayDate(newFrom));
                                            setPreset('custom');
                                            const apiTo = toApiDate(toDate);
                                            if (apiTo && newFrom > apiTo) {
                                                setToDate(toDisplayDate(newFrom));
                                            }
                                        }}
                                        style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fromPickerRef.current && fromPickerRef.current.showPicker()}
                                        disabled={preset !== 'custom' || refreshing || loadingMetrics}
                                        style={{
                                            position: 'absolute',
                                            right: '8px',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: (preset !== 'custom' || refreshing || loadingMetrics) ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: '#64748B',
                                            opacity: (preset !== 'custom' || refreshing || loadingMetrics) ? 0.5 : 1,
                                            padding: 0
                                        }}
                                    >
                                        <Calendar size={14} />
                                    </button>
                                </div>
                            </div>
                            <div className="db-inline-input-wrap">
                                <span className="db-date-label">To:</span>
                                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        placeholder="DD/MM/YYYY"
                                        value={toDisplayDate(toDate)}
                                        onChange={(e) => {
                                            setToDate(e.target.value);
                                            setPreset('custom');
                                        }}
                                        disabled={preset !== 'custom' || refreshing || loadingMetrics}
                                        className="db-inline-date-picker"
                                        style={{ paddingRight: '32px', width: '130px' }}
                                    />
                                    <input
                                        type="date"
                                        ref={toPickerRef}
                                        value={toApiDate(toDate)}
                                        min={toApiDate(fromDate)}
                                        onChange={(e) => {
                                            const newTo = e.target.value;
                                            setToDate(toDisplayDate(newTo));
                                            setPreset('custom');
                                        }}
                                        style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => toPickerRef.current && toPickerRef.current.showPicker()}
                                        disabled={preset !== 'custom' || refreshing || loadingMetrics}
                                        style={{
                                            position: 'absolute',
                                            right: '8px',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: (preset !== 'custom' || refreshing || loadingMetrics) ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: '#64748B',
                                            opacity: (preset !== 'custom' || refreshing || loadingMetrics) ? 0.5 : 1,
                                            padding: 0
                                        }}
                                    >
                                        <Calendar size={14} />
                                    </button>
                                </div>
                            </div>
                            <button
                                className="db-action-btn db-btn-primary db-apply-btn"
                                onClick={() => fetchData(preset, fromDate, toDate)}
                                disabled={(preset === 'custom' ? (!fromDate || !toDate) : !preset) || refreshing || loadingMetrics}
                            >
                                Apply
                            </button>
                        </div>
                    </div>

                    {/* Refresh */}
                    <button
                        className="db-action-btn db-btn-secondary db-icon-btn"
                        onClick={handleRefresh}
                        disabled={refreshing || loadingMetrics}
                        title="Refresh"
                    >
                        <RefreshCw size={14} className={refreshing ? 'spin-icon' : ''} />
                    </button>

                    {/* Export PDF - Show for Team Lead and Managers, Hide for Team Member */}
                    {isManager && (
                        <DashboardExportButton
                            disabled={refreshing || loadingMetrics}
                            data={{
                                preset,
                                fromDate,
                                toDate,
                                activeRFPs,
                                winRate,
                                pipelineValDisplay,
                                achievedValDisplay,
                                urgentDeadlines,
                                bottlenecks,
                                teamWorkload,
                                rfpList
                            }}
                        />
                    )}
                </div>
            </header>

            {/* ── KPI Strip ──────────────────────────────────────────────────── */}
            <section className="db-kpi-grid">
                {isManager ? (
                    <>
                        <KpiCard title="ACTIVE RFPs" value={activeRFPs} icon={FileText} colorClass="blue" loading={loadingMetrics} />
                        <KpiCard title="WIN RATE" value={`${winRate}%`} icon={Target} colorClass="green" loading={loadingMetrics} />
                        <KpiCard title="PIPELINE VALUE" value={pipelineValDisplay} icon={Coins} colorClass="slate" loading={loadingMetrics} />
                        <KpiCard title="ACHIEVED VALUE" value={achievedValDisplay} icon={Award} colorClass="orange" loading={loadingMetrics} />
                    </>
                ) : (
                    <>
                        <KpiCard
                            title="ACTIVE SECTIONS"
                            value={activeSections}
                            icon={FileText}
                            colorClass="blue"
                            loading={loadingMetrics}
                            tooltip="Count of all sections currently assigned with status Assigned, In Progress, Submitted, or Revision Needed in active proposals"
                        />
                        <KpiCard
                            title="COMPLETION RATE"
                            value={typeof completionRate === 'string' && completionRate.includes('%') ? completionRate : `${completionRate}%`}
                            icon={Target}
                            colorClass="green"
                            loading={loadingMetrics}
                            tooltip="(Total Submitted or Approved / Total Assigned) * 100"
                        />
                        <KpiCard
                            title="DUE THIS WEEK"
                            value={dueThisWeek}
                            icon={Clock}
                            colorClass="orange"
                            loading={loadingMetrics}
                            tooltip="Count of your pending sections whose deadline falls between Monday 00:00:00 and Sunday 23:59:59 of the current week"
                        />
                        <KpiCard
                            title="IN REVISION"
                            value={sectionsInRevision}
                            icon={AlertTriangle}
                            colorClass="slate"
                            loading={loadingMetrics}
                            tooltip="Count of sections where the Team Lead requested changes (Status 55: Revision Needed)"
                        />
                    </>
                )}
            </section>

            {/* ── Main Content Grid ───────────────────────────────────────────── */}
            <div className="db-main-grid">

                {/* ── Top Row ─────────────────────────────────────────────── */}
                <div className="db-top-row">

                    {/* Alert Intelligence Column */}
                    <div className="db-alert-col">
                        <SectionCard
                            title={!hasAlerts ? "Team Status" : "Alert Intelligence"}
                            icon={!hasAlerts ? ShieldCheck : ShieldAlert}
                            iconClass={!hasAlerts ? "icon-green" : "icon-red"}
                            badge={hasAlerts && (
                                <span className="db-badge-count">
                                    {allAlertsSorted.length} Pending
                                </span>
                            )}
                        >
                            {loadingMetrics ? (
                                <Skeleton height={72} count={2} />
                            ) : !hasAlerts ? (
                                <div className="db-empty-state-wrapper green-theme">
                                    <div className="db-empty-circle-container">
                                        <span className="sparkle sp-1">✦</span>
                                        <span className="sparkle sp-2">✦</span>
                                        <span className="sparkle sp-3">✦</span>
                                        <span className="sparkle sp-4">✦</span>
                                        <div className="db-empty-circle-inner">
                                            <Check size={28} className="green-check" />
                                        </div>
                                    </div>
                                    <h4 className="db-empty-state-title">Everything is up to date</h4>
                                    <p className="db-empty-state-desc">No active assignments, blockers, revisions, or urgent deadlines. Great job!</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                    <div className="db-alerts-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, overflowY: 'auto' }}>
                                        {displayAlerts.map((item, i) => (
                                            <AlertItem
                                                key={i}
                                                item={item}
                                                type={item.type}
                                                onClick={() => navigate(`/proposals/analysis-result/${item.rfp_id}`)}
                                            />
                                        ))}
                                    </div>
                                    {allAlertsSorted.length > 3 && (
                                        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-start' }}>
                                            <button
                                                className="db-view-all-btn"
                                                onClick={() => setShowAlertsModal(true)}
                                                style={{ fontSize: '12px', fontWeight: '700', color: '#FF5526', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                            >
                                                View All Alerts →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </SectionCard>
                    </div>

                    {/* Team Allocation Column */}
                    <div className="db-team-col">
                        {isManager ? (
                            <SectionCard
                                title={teamWorkload.length === 0 ? "Recent Activity" : "Team Allocation"}
                                icon={teamWorkload.length === 0 ? Inbox : Users}
                                iconClass={teamWorkload.length === 0 ? "icon-blue" : "icon-slate"}
                                className="db-team-card-section"
                            >
                                {loadingMetrics ? (
                                    <Skeleton height={110} count={2} />
                                ) : teamWorkload.length === 0 ? (
                                    <div className="db-empty-state-wrapper gray-theme">
                                        <div className="db-empty-inbox-container">
                                            <Inbox size={48} className="gray-inbox-icon" />
                                        </div>
                                        <h4 className="db-empty-state-title dark-text">No recent Activity</h4>
                                        <p className="db-empty-state-desc">You don't have any active assignments or recent updates.</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                        <div className="db-team-list" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, overflowY: 'auto' }}>
                                            {displayTeamWorkload.map((member, idx) => (
                                                <TeamMemberCard
                                                    key={idx}
                                                    member={member}
                                                    rfpList={rfpList}
                                                    onViewAll={() => {
                                                        setSelectedTeamMember(member);
                                                        setShowTeamModal(true);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-start' }}>
                                            <button
                                                className="db-view-all-btn"
                                                onClick={() => {
                                                    setSelectedTeamMember(null);
                                                    setShowTeamModal(true);
                                                }}
                                                style={{ fontSize: '12px', fontWeight: '700', color: '#64748B', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                            >
                                                View All Team Members →
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </SectionCard>
                        ) : (
                            <SectionCard
                                title={sortedActiveSections.length === 0 ? "Recent Activity" : "Active Assigned Sections"}
                                icon={sortedActiveSections.length === 0 ? Inbox : FileText}
                                iconClass={sortedActiveSections.length === 0 ? "icon-blue" : "icon-slate"}
                                className="db-team-card-section"
                            >
                                {loadingMetrics ? (
                                    <Skeleton height={80} count={2} />
                                ) : sortedActiveSections.length === 0 ? (
                                    <div className="db-empty-state-wrapper gray-theme">
                                        <div className="db-empty-inbox-container">
                                            <Inbox size={48} className="gray-inbox-icon" />
                                        </div>
                                        <h4 className="db-empty-state-title dark-text">No recent Activity</h4>
                                        <p className="db-empty-state-desc">You don't have any active assignments or recent updates.</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                        <div className="db-team-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, overflowY: 'auto' }}>
                                            {displayActiveSections.map((section, idx) => (
                                                <div
                                                    key={idx}
                                                    className="db-team-card"
                                                    style={{ cursor: 'pointer', padding: '10px 12px' }}
                                                    onClick={() => navigate(`/proposals/analysis-result/${section.rfp_id}`)}
                                                >
                                                    <div className="db-team-card-top" style={{ marginBottom: '6px' }}>
                                                        <div className="db-team-info">
                                                            <h5 className="db-team-name" style={{ fontSize: '14px', fontWeight: '700', color: '#1E293B' }}>
                                                                <span style={{ color: '#94A3B8', fontWeight: 600, marginRight: '4px', fontSize: '11px', textTransform: 'uppercase' }}>PROPOSAL:</span>
                                                                {section.proposal_title || '—'}
                                                            </h5>
                                                            <span className="db-team-role" style={{ color: '#475569', display: 'block', marginTop: '4px', fontWeight: '700', fontSize: '12px' }}>
                                                                <span style={{ color: '#94A3B8', fontWeight: 600, marginRight: '4px', fontSize: '11px', textTransform: 'uppercase' }}>SECTION:</span>
                                                                {section.section_title || 'Untitled'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="db-prop-meta" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                        <div className="db-prop-date" style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center' }}>
                                                            <Clock size={11} style={{ marginRight: '4px' }} />
                                                            <span>Due: {section.deadline ? formatDateDMY(section.deadline) : 'TBD'}</span>
                                                        </div>
                                                        <div className={`db-team-status-badge ${getStatusBadgeClass(section.status_code)}`} style={{ margin: 0, padding: '3px 8px', fontSize: '10px' }}>
                                                            {getSectionStatusLabel(section.status_code)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {sortedActiveSections.length > 2 && (
                                            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-start' }}>
                                                <button
                                                    className="db-view-all-btn"
                                                    onClick={() => setShowSectionsModal(true)}
                                                    style={{ fontSize: '12px', fontWeight: '700', color: '#64748B', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                >
                                                    View All Assigned Sections →
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </SectionCard>
                        )}
                    </div>
                </div>

                {/* ── Second Row Charts ─────────────────────────────────────── */}
                <div className="db-charts-row executive-layout">

                    {/* Stage Distribution */}
                    <SectionCard title="Stage Distribution" icon={Layers} iconClass="icon-violet">
                        {loadingMetrics ? <Skeleton height={260} /> : stagePieData.length === 0 ? (
                            <p className="db-empty-text">No stage distribution data available.</p>
                        ) : (
                            <div className="stage-distribution-container">
                                <div className="donut-chart-wrapper">
                                    <div className="donut-center-label" style={{ zIndex: 0 }}>
                                        <span className="donut-center-title">Total RFPs</span>
                                        <span className="donut-center-value">{totalRFPs}</span>
                                    </div>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie
                                                data={stagePieData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={100}
                                                paddingAngle={3}
                                                dataKey="value"
                                            >
                                                {stagePieData.map((entry, idx) => (
                                                    <Cell
                                                        key={`cell-${idx}`}
                                                        fill={entry.color}
                                                        style={{
                                                            filter: activeStageIndex === idx ? 'drop-shadow(0px 0px 6px rgba(0,0,0,0.15))' : 'none',
                                                            transition: 'all 0.2s ease',
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip content={<CustomPieTooltip />} wrapperStyle={{ zIndex: 100 }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="stage-legend-list">
                                    {stagePieData.map((item, idx) => {
                                        const pct = totalRFPs > 0 ? ((item.value / totalRFPs) * 100).toFixed(0) : 0;
                                        return (
                                            <div
                                                key={idx}
                                                className={`stage-legend-item ${activeStageIndex === idx ? 'active' : ''}`}
                                                onMouseEnter={() => setActiveStageIndex(idx)}
                                                onMouseLeave={() => setActiveStageIndex(null)}
                                            >
                                                <span className="legend-dot" style={{ backgroundColor: item.color }} />
                                                <span className="legend-name">{item.name}</span>
                                                <span className="legend-stats">
                                                    <span className="legend-count">{item.value}</span>
                                                    {/* <span className="legend-pct">({pct}%)</span> */}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </SectionCard>

                    {/* Trends (Win Rate Trends for Manager, Productivity Trends for Employee) */}
                    {isManager ? (
                        <SectionCard title="Win Rate Trends" icon={TrendingUp} iconClass="icon-green">
                            {loadingMetrics ? <Skeleton height={260} /> :
                                winTrends.length === 0 ? <p className="db-empty-text">No trend data.</p> : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <AreaChart data={winTrends} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="winGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#FF5526" stopOpacity={0.18} />
                                                    <stop offset="100%" stopColor="#FF5526" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                            <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: '#94A3B8', fontSize: 10 }} />
                                            <YAxis tickLine={false} axisLine={false} tick={{ fill: '#94A3B8', fontSize: 11 }} unit="%" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} />
                                            <Tooltip content={<CustomTooltip unit="%" />} />
                                            <Area type="monotone" dataKey="win_rate" stroke="#FF5526" strokeWidth={2.5} fill="url(#winGrad)" dot={{ r: 3, fill: '#FF5526', strokeWidth: 0 }} activeDot={{ r: 5, fill: '#FF5526' }} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                )
                            }
                        </SectionCard>
                    ) : (
                        <SectionCard title="Productivity Trends" icon={TrendingUp} iconClass="icon-green">
                            {loadingMetrics ? <Skeleton height={260} /> :
                                prodTrends.length === 0 ? <p className="db-empty-text">No productivity data.</p> : (
                                    <ResponsiveContainer width="100%" height={260}>
                                        <AreaChart data={prodTrends} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="prodGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.18} />
                                                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                            <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: '#94A3B8', fontSize: 10 }} />
                                            <YAxis tickLine={false} axisLine={false} tick={{ fill: '#94A3B8', fontSize: 11 }} allowDecimals={false} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area type="monotone" dataKey="submitted_sections" stroke="#10B981" strokeWidth={2.5} fill="url(#prodGrad)" dot={{ r: 3, fill: '#10B981', strokeWidth: 0 }} activeDot={{ r: 5, fill: '#10B981' }} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                )
                            }
                        </SectionCard>
                    )}
                </div>
            </div>

            {/* Team Allocation Pop-up Modal */}
            {showTeamModal && (
                <div className="db-modal-overlay" onClick={() => { setShowTeamModal(false); setSelectedTeamMember(null); }}>
                    <div className="db-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="db-modal-header">
                            <div className="db-modal-title-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div className="db-section-icon icon-slate">
                                    <Users size={16} />
                                </div>
                                <h3 className="db-modal-title" style={{ fontSize: '16px', fontWeight: '700', color: '#1E293B' }}>
                                    {selectedTeamMember ? `${capitalizeWords(selectedTeamMember.full_name)}'s Allocations` : 'All Team Allocations'}
                                </h3>
                            </div>
                            <button className="db-modal-close-btn" onClick={() => { setShowTeamModal(false); setSelectedTeamMember(null); }} title="Close">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="db-modal-body">
                            <div className="db-team-list" style={{ gap: '14px' }}>
                                {(selectedTeamMember ? [selectedTeamMember] : teamWorkload).map((member, idx) => (
                                    <TeamMemberCard key={idx} member={member} rfpList={rfpList} isFull={true} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View All Alerts Modal */}
            {showAlertsModal && (
                <div className="db-modal-overlay" onClick={() => setShowAlertsModal(false)}>
                    <div className="db-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%' }}>
                        <div className="db-modal-header">
                            <div className="db-modal-title-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div className="db-section-icon icon-red">
                                    <ShieldAlert size={16} />
                                </div>
                                <h3 className="db-modal-title" style={{ fontSize: '16px', fontWeight: '700', color: '#1E293B' }}>All Alerts ({allAlertsSorted.length})</h3>
                            </div>
                            <button className="db-modal-close-btn" onClick={() => setShowAlertsModal(false)} title="Close">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="db-modal-body" style={{ padding: '20px' }}>
                            <div className="db-alerts-grid" style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '4px' }}>
                                <div className="db-alerts-col">
                                    {allAlertsSorted.filter((_, i) => i % 2 === 0).map((item, i) => (
                                        <AlertItem key={`modal-col1-${i}`} item={item} type={item.type} onClick={() => navigate(`/proposals/analysis-result/${item.rfp_id}`)} />
                                    ))}
                                </div>
                                <div className="db-alerts-col">
                                    {allAlertsSorted.filter((_, i) => i % 2 !== 0).map((item, i) => (
                                        <AlertItem key={`modal-col2-${i}`} item={item} type={item.type} onClick={() => navigate(`/proposals/analysis-result/${item.rfp_id}`)} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View All Assigned Sections Modal */}
            {showSectionsModal && (
                <div className="db-modal-overlay" onClick={() => setShowSectionsModal(false)}>
                    <div className="db-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
                        <div className="db-modal-header">
                            <div className="db-modal-title-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div className="db-section-icon icon-slate">
                                    <FileText size={16} />
                                </div>
                                <h3 className="db-modal-title" style={{ fontSize: '16px', fontWeight: '700', color: '#1E293B' }}>All Assigned Sections ({sortedActiveSections.length})</h3>
                            </div>
                            <button className="db-modal-close-btn" onClick={() => setShowSectionsModal(false)} title="Close">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="db-modal-body" style={{ padding: '20px' }}>
                            <div className="db-team-list" style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '4px', gap: '12px' }}>
                                {sortedActiveSections.map((section, idx) => (
                                    <div
                                        key={`modal-${idx}`}
                                        className="db-team-card"
                                        style={{ cursor: 'pointer', padding: '14px', border: '1px solid #E8EDF3' }}
                                        onClick={() => navigate(`/proposals/analysis-result/${section.rfp_id}`)}
                                    >
                                        <div className="db-team-card-top" style={{ marginBottom: '8px' }}>
                                            <div className="db-team-info">
                                                <h5 className="db-team-name" style={{ fontSize: '14px', fontWeight: '700', color: '#1E293B' }}>
                                                    <span style={{ color: '#94A3B8', fontWeight: 600, marginRight: '4px', fontSize: '11px', textTransform: 'uppercase' }}>PROPOSAL:</span>
                                                    {section.proposal_title || '—'}
                                                </h5>
                                                <span className="db-team-role" style={{ color: '#475569', display: 'block', marginTop: '4px', fontWeight: '700', fontSize: '12px' }}>
                                                    <span style={{ color: '#94A3B8', fontWeight: 600, marginRight: '4px', fontSize: '11px', textTransform: 'uppercase' }}>SECTION:</span>
                                                    {section.section_title || 'Untitled'}
                                                </span>
                                            </div>
                                            <div className={`db-team-status-badge ${getStatusBadgeClass(section.status_code)}`} style={{ margin: 0, padding: '4px 10px', fontSize: '11px' }}>
                                                {getSectionStatusLabel(section.status_code)}
                                            </div>
                                        </div>
                                        <div className="db-prop-meta" style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                                            <div className="db-prop-date" style={{ fontSize: '12px', color: '#64748B', display: 'flex', alignItems: 'center' }}>
                                                <Clock size={12} style={{ marginRight: '6px' }} />
                                                <span>Due: {section.deadline ? formatDateDMY(section.deadline) : 'TBD'}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Active Proposals Board - Full Width */}
            {isManager && (
                <div className="db-full-width-proposals" style={{ marginTop: '24px' }}>
                    <SectionCard
                        title="Active Proposals"
                        action={
                            <button
                                className="db-view-all-btn"
                                onClick={() => navigate(isManager ? '/proposals' : '/my-proposals')}
                            >
                                View all <ArrowRight size={13} />
                            </button>
                        }
                    >
                        {loadingRFPs ? (
                            <div className="db-prop-grid"><Skeleton height={130} count={3} /></div>
                        ) : rfpList.length === 0 ? (
                            <div className="db-empty-proposals">
                                <FileText size={32} className="db-empty-icon" />
                                <p>No active proposals found.</p>
                            </div>
                        ) : (
                            <div className="db-prop-grid">
                                {rfpList.slice(0, 6).map((p) => (
                                    <ProposalBoardCard
                                        key={p.rfp_id || p.id}
                                        proposal={p}
                                        isManager={isManager}
                                        onClick={() => navigate(`/proposals/analysis-result/${p.rfp_id || p.id}`, {
                                            state: { rfpId: p.rfp_id || p.id }
                                        })}
                                    />
                                ))}
                            </div>
                        )}
                    </SectionCard>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
